// Favoriten-Liste pro User. Speicherung in Firestore unter users/{uid}/profile/watchlist
// → Geräte-übergreifend sync. Optimistic Update lokal, Persist asynchron.
import { useCallback, useEffect, useState } from 'react';
import { doc, getDoc, setDoc } from '../lib/fsCompat';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';

const LOCAL_KEY = 'red_favorites_v1';

function readLocal() {
  try {
    const raw = localStorage.getItem(LOCAL_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.filter(s => typeof s === 'string') : [];
  } catch { return []; }
}

function writeLocal(syms) {
  try { localStorage.setItem(LOCAL_KEY, JSON.stringify(syms)); } catch {}
}

export function useFavorites() {
  const { user } = useAuth();
  const [favorites, setFavorites] = useState(() => readLocal());
  const [hydrated, setHydrated] = useState(false);

  // Initial: Firestore lesen wenn user da ist, sonst Local-Werte beibehalten
  useEffect(() => {
    let alive = true;
    (async () => {
      if (!user?.uid) { setHydrated(true); return; }
      try {
        const ref = doc(db, 'users', user.uid, 'profile', 'watchlist');
        const snap = await getDoc(ref);
        if (!alive) return;
        if (snap.exists()) {
          const remote = snap.data()?.syms;
          if (Array.isArray(remote)) {
            // Merge Local + Remote (Union) damit ein User auf neuem Gerät vorhandene Local-Favs nicht verliert.
            const merged = Array.from(new Set([...remote, ...readLocal()]));
            setFavorites(merged);
            writeLocal(merged);
            // Falls Merge mehr Einträge als Remote hatte → zurückschreiben
            if (merged.length !== remote.length) {
              setDoc(ref, { syms: merged, updatedAt: Date.now() }, { merge: true }).catch(() => null);
            }
          }
        } else if (readLocal().length > 0) {
          // Erstes Mal: Local nach Firestore migrieren
          setDoc(ref, { syms: readLocal(), updatedAt: Date.now() }, { merge: true }).catch(() => null);
        }
      } catch {
        // offline / rules — Local-Liste bleibt aktiv
      } finally {
        if (alive) setHydrated(true);
      }
    })();
    return () => { alive = false; };
  }, [user?.uid]);

  const toggleFavorite = useCallback((sym) => {
    if (!sym) return;
    setFavorites(prev => {
      const has = prev.includes(sym);
      const next = has ? prev.filter(s => s !== sym) : [...prev, sym];
      writeLocal(next);
      if (user?.uid) {
        const ref = doc(db, 'users', user.uid, 'profile', 'watchlist');
        setDoc(ref, { syms: next, updatedAt: Date.now() }, { merge: true }).catch(() => null);
      }
      return next;
    });
  }, [user?.uid]);

  const isFavorite = useCallback((sym) => favorites.includes(sym), [favorites]);

  return { favorites, toggleFavorite, isFavorite, hydrated };
}
